import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { ArrowLeft, Search, MapPin, Mountain, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';

import DestinationCard    from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/DestinationCard';
import InfoCard           from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/InfoCard';
import GalleryImage       from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/GalleryImage';
import InteractiveMap     from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/InteractiveMap';
import WeatherWidget      from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/WeatherWidget';
import FamousSpots        from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/FamousSpots';
import RecommendedDestinations from '../../../../components/customer/PublicCustomerScreens/DestinationsCard/RecommendedDestinations';
import CustomerLoginForm from '../../../../components/customer/PublicCustomerScreens/CustomerFormComponents/CustomerLoginForm';

import '../../../../assets/css/customer/publicCustomerCss/pubCusDestinationDetail/destination.css';

const CATEGORIES = ['All', 'Mountains', 'Lakes', 'Rivers', 'Historical', 'Desert', 'National Park', 'Valleys'];

const DestinationDetail = () => {
  const { baseUrl, imageBaseUrl, authToken } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [destinations, setDestinations]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState(null);
  const [searchTerm, setSearchTerm]       = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [wishlistIds, setWishlistIds]     = useState([]);

  useEffect(() => {
    axios.get(`${baseUrl}/customer/viewDestinationCustomer`)
      .then((res) => {
        if (res.data.status) {
          setDestinations(res.data.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fetch packages error:', err);
        navigate('/connection-lost');
      });
  }, [baseUrl]);

useEffect(() => {
    if (!authToken) return;

    const currentCustomerId = Number(localStorage.getItem('customerId'));

    axios.get(`${baseUrl}/customer/wishlist`, {
        headers: { Authorization: `Bearer ${authToken}` }
    })
        .then((res) => {
            if (res.data.success) {
                const destIds = res.data.data
                    .filter((item) => Number(item.jjcustomer_id) === currentCustomerId)
                    .filter((item) => item.jjdestination_id !== null)
                    .map((item) => Number(item.jjdestination_id));
                setWishlistIds(destIds);
            }
        })
        .catch((err) => {
            console.error('Fetch wishlist error:', err);
        });
}, [baseUrl, authToken]);

  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam && destinations.length > 0) {
      const found = destinations.find((d) => String(d.id) === String(idParam));
      if (found) setSelected(found);
    }
  }, [searchParams, destinations]);

  useEffect(() => {
    if (selected) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selected]);

  
  const activeDestinations = destinations.filter(
    (dest) => !dest.status || dest.status === 'Active'
  );

  const filtered = activeDestinations.filter((dest) => {
    const term = searchTerm.toLowerCase();
    const matchSearch =
      !term ||
      dest.destination_name.toLowerCase().includes(term) ||
      dest.province.toLowerCase().includes(term) ||
      dest.district.toLowerCase().includes(term);

    const matchCat =
      categoryFilter === 'All' ||
      (dest.description && dest.description.toLowerCase().includes(categoryFilter.toLowerCase()));

    return matchSearch && matchCat;
  });

const handleWishlistToggle = (destId, e) => {
    e && e.stopPropagation();

    if (!authToken) {
        setShowLoginModal(true);
        return;
    }

    const numericDestId = Number(destId);
    const isWishlisted = wishlistIds.includes(numericDestId);

    if (isWishlisted) {
        axios.delete(`${baseUrl}/customer/wishlist/${numericDestId}`, {
            headers: { Authorization: `Bearer ${authToken}` }
        })
            .then(() => {
                setWishlistIds((prev) => prev.filter((id) => id !== numericDestId));
            })
            .catch(() => {});
    } else {
        const formData = new FormData();
        formData.append('jjdestination_id', numericDestId);

        axios.post(`${baseUrl}/customer/addWishlist`, formData, {
            headers: { Authorization: `Bearer ${authToken}` }
        })
            .then(() => {
                setWishlistIds((prev) => [...prev, numericDestId]);
            })
            .catch(() => {});
    }
};

  const imgUrl = (path) => (path ? `${imageBaseUrl}/${path}` : null);

  
  if (selected) {
    const spots   = selected.jjdestinationspots   || [];
    const gallery = selected.jjdestinationgallerys || [];

  
    const heroSrc =
      imgUrl(selected.image) ||
      imgUrl(gallery[0]?.des_gallery) ||
      null;

    return (
      <>
        <div className="pub-cus-des-detail-bg pb-5">

          <div className="pub-cus-des-detail-topbar">
            <Container>
              <div className="d-flex align-items-center justify-content-between">
                <button
                  className="pub-cus-des-back-btn"
                  onClick={() => {
                    setSelected(null);
                    setSearchParams({});
                  }}
                >
                  <ArrowLeft size={16} />
                  Back to Explorer
                </button>

                <button
               className={`pub-cus-des-detail-wishlist-btn ${wishlistIds.includes(Number(selected.id)) ? 'saved' : ''}`}                  onClick={(e) => handleWishlistToggle(selected.id, e)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  {wishlistIds.includes(Number(selected.id)) ? 'Saved' : 'Add to Wishlist'}
                </button>
              </div>
            </Container>
          </div>

          <Container className="py-4 py-md-5">
            <Row className="g-4">

              <Col lg={8}>
                <div className="pub-cus-des-detail-hero mb-4">
                  {heroSrc ? (
                    <img
                      src={heroSrc}
                      alt={selected.destination_name}
                      className="pub-cus-des-detail-hero-img"
                      onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                    />
                  ) : (
                    <div className="pub-cus-des-detail-hero-placeholder">
                      <ImageIcon size={44} />
                    </div>
                  )}
                  <div className="pub-cus-des-detail-hero-overlay">
                    <div>
                      <h1 className="fw-bold text-white mb-1" style={{ fontSize: 'clamp(1.5rem,4vw,2.5rem)' }}>
                        {selected.destination_name}
                      </h1>
                      <p className="text-white mb-0 opacity-90 d-flex align-items-center gap-1">
                        <MapPin size={15} />
                        {selected.district}, {selected.province}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pub-cus-des-info-row mb-4">
                  <InfoCard icon="mountain" label="Climate"     value={selected.climate       || '—'} />
                  <InfoCard icon="sun"      label="Best Season" value={selected.best_season    || '—'} />
                  <InfoCard icon="clock"    label="Recommended" value={selected.recommended_duration ? `${selected.recommended_duration} days` : '—'} />
                </div>

                <div className="pub-cus-des-section-card">
                  <h2 className="pub-cus-des-section-title">
                    <span className="pub-cus-des-section-title-icon">
                      <MapPin size={16} />
                    </span>
                    About this Destination
                  </h2>

                  <div className="pub-cus-des-meta-grid mb-4">
                    <div className="pub-cus-des-meta-item">
                      <div className="pub-cus-des-meta-item-label">Province</div>
                      <div className="pub-cus-des-meta-item-value">{selected.province}</div>
                    </div>
                    <div className="pub-cus-des-meta-item">
                      <div className="pub-cus-des-meta-item-label">District</div>
                      <div className="pub-cus-des-meta-item-value">{selected.district}</div>
                    </div>
                    <div className="pub-cus-des-meta-item">
                      <div className="pub-cus-des-meta-item-label">Nearest City</div>
                      <div className="pub-cus-des-meta-item-value">{selected.nearest_city || '—'}</div>
                    </div>
                    <div className="pub-cus-des-meta-item">
                      <div className="pub-cus-des-meta-item-label">Coordinates</div>
                      <div className="pub-cus-des-meta-item-value" style={{ color: '#4A9E8E' }}>
                        {selected.latitude && selected.longitude
                          ? `${parseFloat(selected.latitude).toFixed(4)}° N, ${parseFloat(selected.longitude).toFixed(4)}° E`
                          : '—'}
                      </div>
                    </div>
                    {selected.min_budget && (
                      <div className="pub-cus-des-meta-item">
                        <div className="pub-cus-des-meta-item-label">Est. Budget</div>
                        <div className="pub-cus-des-meta-item-value">
                          PKR {Number(selected.min_budget).toLocaleString()} – {Number(selected.max_budget).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>

                  {selected.description && (
                    <p style={{ color: '#4a5568', lineHeight: 1.7, fontSize: '0.95rem' }}>
                      {selected.description}
                    </p>
                  )}
                </div>

                <div className="pub-cus-des-section-card">
                  <h2 className="pub-cus-des-section-title">
                    <span className="pub-cus-des-section-title-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                    </span>
                    Weather Conditions
                  </h2>
                  <WeatherWidget destinationName={selected.destination_name} weather={selected.weather} />
                </div>

                {selected.latitude && selected.longitude && (
                  <div className="pub-cus-des-section-card">
                    <h2 className="pub-cus-des-section-title">
                      <span className="pub-cus-des-section-title-icon">
                        <MapPin size={16} />
                      </span>
                      Location Map
                    </h2>
                    <div className="pub-cus-des-map-container">
                      <InteractiveMap
                        lat={parseFloat(selected.latitude)}
                        lng={parseFloat(selected.longitude)}
                        destinationName={selected.destination_name}
                        spots={spots}
                      />
                    </div>
                  </div>
                )}

                {spots.length > 0 && (
                  <div className="pub-cus-des-section-card">
                    <h2 className="pub-cus-des-section-title">
                      <span className="pub-cus-des-section-title-icon">
                        <Mountain size={16} />
                      </span>
                      Famous Spots
                    </h2>
                    <FamousSpots spots={spots} imageBaseUrl={imageBaseUrl} />
                  </div>
                )}
                {gallery.length > 0 && (
                  <div className="pub-cus-des-section-card">
                    <GalleryImage images={gallery} imageBaseUrl={imageBaseUrl} />
                  </div>
                )}
              </Col>

              <Col lg={4}>
                <div className="pub-cus-des-sidebar-sticky">
                  <RecommendedDestinations
                    currentDestination={selected}
                    allDestinations={activeDestinations}
                    onSelect={setSelected}
                    imageBaseUrl={imageBaseUrl}
                  />
                </div>
              </Col>

            </Row>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pub-cus-des-list-screen">

        <div className="pub-cus-des-hero">
          <Container>
            <div className="pub-cus-des-hero-eyebrow">
              <MapPin size={12} />
              Pakistan's Northern Wonders
            </div>
            <h1 className="pub-cus-des-hero-title">
              Discover Your<br />
              <span>Next Adventure</span>
            </h1>
            <p className="pub-cus-des-hero-sub">
              Explore breathtaking valleys, ancient forts, and pristine lakes of Pakistan
            </p>
            <div className="pub-cus-des-stats-bar">
              <div className="pub-cus-des-stat">
                <div className="pub-cus-des-stat-number">{activeDestinations.length}+</div>
                <div className="pub-cus-des-stat-label">Destinations</div>
              </div>
              <div className="pub-cus-des-stat">
                <div className="pub-cus-des-stat-number">4</div>
                <div className="pub-cus-des-stat-label">Provinces</div>
              </div>
              <div className="pub-cus-des-stat">
                <div className="pub-cus-des-stat-number">K2</div>
                <div className="pub-cus-des-stat-label">World's 2nd Highest</div>
              </div>
            </div>
          </Container>
        </div>
        <div className="pub-cus-des-search-section">
          <Container>
            <div className="pub-cus-des-search-card">
              <div className="pub-cus-des-search-input-wrapper">
                <Search size={18} className="pub-cus-des-search-icon" />
                <input
                  className="pub-cus-des-search-input"
                  placeholder="Search by name, province, district…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="pub-cus-des-search-clear"
                    onClick={() => setSearchTerm('')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="pub-cus-des-filters">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    className={`pub-cus-des-filter-btn ${categoryFilter === cat ? 'active' : ''}`}
                    onClick={() => setCategoryFilter(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </Container>
        </div>
        <div className="pub-cus-des-grid-section">
          <Container>
            <p className="pub-cus-des-results-meta">
              {loading
                ? 'Loading destinations…'
                : `Showing ${filtered.length} destination${filtered.length !== 1 ? 's' : ''}`}
            </p>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-light" />
              </div>
            ) : filtered.length > 0 ? (
              <Row className="g-4">
                {filtered.map((dest) => (
                  <Col key={dest.id} lg={4} md={6} sm={12}>
                    <DestinationCard
                      destination={dest}
                      imageBaseUrl={imageBaseUrl}
                      isWishlisted={wishlistIds.includes(Number(dest.id))}
                      onExplore={() => setSelected(dest)}
                      onWishlist={(e) => handleWishlistToggle(dest.id, e)}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="pub-cus-des-empty">
                <div className="pub-cus-des-empty-icon">
                  <Search size={28} />
                </div>
                <h4>No destinations found</h4>
                <p>Try adjusting your search or filter criteria</p>
                <button
                  className="pub-cus-des-clear-btn"
                  onClick={() => { setSearchTerm(''); setCategoryFilter('All'); }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </Container>
        </div>
      </div>
    </>
  );
};

export default DestinationDetail;